import { CollectionQuery, DataService, WithDocId } from '../data.service';
import { DataPathsService } from '../data-paths.service';
import { switchMap, map, filter } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { RouteDataService } from '../route';
import { Injectable } from '@angular/core';
import { CarrierDoc } from './carrier-types';
import { SlServicesService } from '../../sl-services/sl-services.service';
import { PlanningState } from 'src/app/planning/state';
import { DataEnvService } from '../data-env.service';
import { CarrierWorker, CarrierWorkerRole } from 'src/typings/user';

@Injectable()
export class CarrierData {
    constructor(
        private dataCtrl: DataService,
        private dataPaths: DataPathsService,
        private routeData: RouteDataService,
        private services: SlServicesService,
        private dataEnv: DataEnvService
    ) {

    }
    public carriers$ = combineLatest([this.dataPaths.carriers$, this.dataEnv.selectedHub$])
    .pipe(
        switchMap(([path, selectedHub])=>this.dataCtrl.getCollection$<CarrierDoc>({ 
            path, 
            queries: [selectedHub ? ['hub', '==', selectedHub] : null].filter(Boolean) as CollectionQuery })),
        map(docs => docs.map(doc => new Carrier(doc)))
    );

    public carriersWorkers$ = this.dataPaths.carrierWorkers$
    .pipe(
        switchMap((path)=>{
            return this.dataCtrl.getCollection$<CarrierWorker>({path})
        })
    );

    public carriersWorkerRoles$ = this.dataPaths.carrierWorkerRoles$
    .pipe(
        switchMap((path)=>{
            return this.dataCtrl.getCollection$<CarrierWorkerRole>({path})
        })
    );

    public getCarrier$ = (id: string) => {
        return this.carriers$.pipe(map(carriers => carriers.filter(x => x.id === id)[0])
        );
    }

    public getUnassignedCarriers$(date: string = null, currentDateStops$, selectedHub: string) {
        return combineLatest(
            this.routeData.getRoutes$(date, currentDateStops$, selectedHub),
            this.carriers$
        ).pipe(
            map(([routes, carriers]) => {
                return carriers.filter(carrier => !routes.find(route => route.carrierId === carrier.id));
            })
        );
    }

    public async updateCarrier(carrier: Carrier, updateRoute: boolean) {
        await this.services.callService(`updateCarrier`, {carrier, updateRoute});
    }

    public async deleteCarrier(carrierId: string) {
        await this.services.callService(`deleteCarrier`, carrierId);
    }

    public async createCarrierUser(carrierId: string, email: string, password: string ) {
        await this.services.callService(`createCarrierUser`, { carrierId, email, password });
    }

    public async updateCarrierUser(carrierId: string, userId: string, email: string, password: string ) {
        await this.services.callService(`updateCarrierUser`, { carrierId, userId, email, password });
    }

    public async deleteCarrierUser(carrierId: string, userId: string ) {
        await this.services.callService(`deleteCarrierUser`, { carrierId, userId });
    }

    public async createCarrierWorker(firstName: string, lastName: string) {
        await this.services.callService(`saveCarrierWorker`, { firstName, lastName });
    }

    public async updateCarrierWorker(id: string, firstName: string, lastName: string) {
        await this.services.callService(`saveCarrierWorker`, { id, firstName, lastName });
    }

    public async deleteCarrierWorker(id: string) {
        await this.services.callService(`deleteCarrierWorker`, { id });
    }

    public async createCarrierWorkerRole(name: string, description: string) {
        await this.services.callService(`saveCarrierWorkerRole`, { name, description });
    }

    public async updateCarrierWorkerRole(id: string, name: string, description: string) {
        await this.services.callService(`saveCarrierWorkerRole`, { id, name, description });
    }

    public async deleteCarrierWorkerRole(id: string) {
        await this.services.callService(`deleteCarrierWorkerRole`, { id });
    }    
}

export class Carrier {
    constructor(
        private carrierDoc: WithDocId<CarrierDoc>
    ) { }

    public id = this.carrierDoc.docId;
    public name = this.carrierDoc.name;
    public active = this.carrierDoc.active;
    public virtual = this.carrierDoc.virtual;
    public cubicFeet = this.carrierDoc.cubic_feet;
    public startTime = this.carrierDoc.defaults.startTime;
    public endTime = this.carrierDoc.defaults.endTime;
    public installer = this.carrierDoc.installed_on_truck !== 'false' && this.carrierDoc.installed_on_truck;
    public color = this.carrierDoc.color;
}